import { useEffect, useRef, useState } from 'react';

interface Region { region: string; states: string; }

interface ServiceAreasProps {
  regions: Region[];
  title?: string;
  subtitle?: string;
  showNational?: boolean;
}

function useReveal() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.1 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return { ref, visible };
}

export default function ServiceAreas({
  regions,
  title = 'Nationwide Coverage',
  subtitle = 'Headquartered in Lake Forest, CA — deployed everywhere. OTT operates across all 50 states with a national crew infrastructure built for programs at any scale.',
  showNational = true,
}: ServiceAreasProps) {
  const { ref, visible } = useReveal();

  return (
    <section
      ref={ref}
      className="py-28 px-6 lg:px-12 xl:px-16 relative overflow-hidden"
      style={{ background: 'linear-gradient(135deg, #080808 0%, #0f0d09 100%)' }}
    >
      {/* Decorative Background Text */}
      <div
        className="absolute top-0 right-0 select-none pointer-events-none overflow-hidden"
        style={{ opacity: 0.03 }}
        aria-hidden="true"
      >
        <span className="font-condensed text-white text-[20vw] font-black leading-none uppercase tracking-tighter whitespace-nowrap">
          USA
        </span>
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        <div className={`mb-16 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <div className="flex items-center gap-3 mb-4">
            <span className="w-10 h-px bg-gold" />
            <span className="text-gold text-[10px] font-sans tracking-[0.35em] uppercase font-semibold">Coverage</span>
          </div>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <h2 className="font-serif text-white text-4xl lg:text-5xl font-bold leading-tight max-w-lg">{title}</h2>
            <p className="text-white/40 text-base font-sans leading-relaxed max-w-md lg:text-right">{subtitle}</p>
          </div>
        </div>

        {showNational && (
          <div className={`flex items-center gap-6 mb-16 p-8 border border-gold/20 bg-gold/5 transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <div className="w-16 h-16 flex items-center justify-center border border-gold/40 bg-gold/10 shrink-0">
              <i className="ri-earth-line text-gold text-2xl" />
            </div>
            <div>
              <div className="font-condensed text-white text-2xl font-bold tracking-wider uppercase mb-1">
                All 50 States
              </div>
              <p className="text-white/40 text-sm font-sans">
                National crew network. Multi-city coordination. Any market, any timeline. OTT scales with your program.
              </p>
            </div>
            <div className="ml-auto hidden lg:flex flex-col items-end gap-1">
              <span className="text-gold text-3xl font-condensed font-black">50</span>
              <span className="text-white/30 text-[10px] font-sans tracking-widest uppercase">States Served</span>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-white/5">
          {regions.map((r, i) => (
            <div
              key={r.region}
              className={`bg-[#0d0d0d] p-6 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: `${i * 60}ms` }}
            >
              <h4 className="font-sans text-white/80 text-sm font-semibold mb-2 uppercase tracking-wider">{r.region}</h4>
              <p className="text-gold/50 text-xs font-sans leading-relaxed">{r.states}</p>
            </div>
          ))}
        </div>

        <p className={`text-center text-white/20 text-xs font-sans mt-8 tracking-widest uppercase transition-all duration-700 delay-500 ${visible ? 'opacity-100' : 'opacity-0'}`}>
          HQ: Lake Forest, CA · Deployed Nationwide · (949) 309-7525
        </p>
      </div>
    </section>
  );
}
